import { Trans } from 'react-i18next';

import { Command } from '@dreryk/command-query-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { Bean, PromiseVoidFunction, TranslatableText } from '@dreryk/react-common';
import {
   dispatchSnackbarPositiveEvent,
   Link,
   ModalWidth,
   RemoveIcon,
   ShowModalEventInterceptor,
   StrongText,
} from '@dreryk/react-components';

import { ExtractItemId } from '../FormsTemplates.types';
import { useFormsTemplatesContext } from '../formsTemplatesContext';
import { formsTemplatesTranslationKeys } from '../translations';

type FormsTemplatesListRemoveTemplateProps<ITEM extends Bean> = {
   readonly templateId: ExtractItemId<ITEM>;
   readonly templateName: string;
   readonly onSuccess: PromiseVoidFunction;
   readonly confirmationModalTitle: TranslatableText;
   readonly successMessage: TranslatableText;
   readonly commandCreator: <RESULT>(templateId: ExtractItemId<ITEM>) => Command<RESULT>;
};

export const FormsTemplatesListRemoveTemplate = <ITEM extends Bean>(
   props: FormsTemplatesListRemoveTemplateProps<ITEM>
): JSX.Element => {
   const { removeItem, activeItem, setReadOnlyItem } = useFormsTemplatesContext<ITEM>();
   const { executeAsync: removeTemplate } = useCqrsCommand<void, void>(() => props.commandCreator(props.templateId), {
      onSuccess: () => {
         dispatchSnackbarPositiveEvent(props.successMessage);
         removeItem(props.templateId);
         if (activeItem?.id === props.templateId) {
            setReadOnlyItem(undefined);
         }
         props.onSuccess();
      },
   });
   return (
      <ShowModalEventInterceptor
         title={props.confirmationModalTitle}
         content={
            <Trans
               i18nKey={formsTemplatesTranslationKeys.areYourSureRemovingTemplate}
               components={{ strong: <StrongText testId={'removeTemplate'} /> }}
               values={{
                  templateName: props.templateName,
               }}
            />
         }
         testId={'removeTemplate'}
         confirmationButtonLabel={formsTemplatesTranslationKeys.remove}
         modalWidth={ModalWidth.NORMAL}
         shouldShowModal
      >
         <Link testId={'removeTemplate'} icon={<RemoveIcon />} onClick={async () => removeTemplate()} />
      </ShowModalEventInterceptor>
   );
};
